import { API_URL } from '../config';
import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Bot, User, Loader2, Maximize2, Minimize2, Trash2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import axios from 'axios';

const FloatingChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Ola! Sou o assistente virtual. Como posso ajudar?'
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  const loadingTimerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const loadingSteps = [
    'Analisando sua pergunta...',
    'Consultando dados do sistema...',
    'Processando informacoes...',
    'Gerando resposta...',
  ];


  // Detectar contexto de atendimento pela URL
  const getAtendimentoIdFromUrl = () => {
    const match = window.location.pathname.match(/\/atendimento\/(\d+)/);
    return match ? parseInt(match[1]) : null;
  };

  // Scroll para ultima mensagem
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focar input quando abrir
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');

    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);
    setLoadingStep(0);

    // Avançar etapas de loading a cada 3 segundos
    let step = 0;
    loadingTimerRef.current = setInterval(() => {
      step++;
      if (step < loadingSteps.length) {
        setLoadingStep(step);
      }
    }, 3000);

    try {
      const historico = messages.slice(-10).map(m => ({
        role: m.role,
        content: m.content
      }));

      const cpfMatch = userMessage.match(/\d{3}[\.-]?\d{3}[\.-]?\d{3}[\.-]?\d{2}/);
      const atendimentoId = getAtendimentoIdFromUrl();

      const response = await axios.post(`${API_URL}/ai/chat`, {
        mensagem: userMessage,
        historico: historico,
        contexto_beneficiario_cpf: cpfMatch ? cpfMatch[0] : null,
        contexto_atendimento_id: atendimentoId
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.data.resposta,
        tool: response.data.tool_usada
      }]);

    } catch (error) {
      console.error('Erro no chat:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Desculpe, ocorreu um erro. Tente novamente.'
      }]);
    } finally {
      clearInterval(loadingTimerRef.current);
      setLoading(false);
      setLoadingStep(0);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([{
      role: 'assistant',
      content: 'Conversa limpa. Como posso ajudar?'
    }]);
  };

  // Tamanhos dinamicos
  const chatSize = isExpanded
    ? { width: '700px', height: '80vh', bottom: '24px', right: '24px' }
    : { width: '400px', height: '520px', bottom: '100px', right: '24px' };

  return (
    <>
      {/* Botao flutuante */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          width: '56px',
          height: '56px',
          borderRadius: '50%',
          border: 'none',
          backgroundColor: isOpen ? '#e74c3c' : 'var(--brand, #20a490)',
          color: isOpen ? '#fff' : 'var(--brand-ink, #fff)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
          zIndex: 10000,
          transition: 'all 0.3s ease',
        }}
      >
        {isOpen ? <X size={22} /> : <MessageCircle size={22} />}
      </button>

      {/* Chat window */}
      {isOpen && (
        <div style={{
          position: 'fixed',
          bottom: chatSize.bottom,
          right: chatSize.right,
          width: chatSize.width,
          height: chatSize.height,
          maxHeight: '90vh',
          maxWidth: 'calc(100vw - 48px)',
          backgroundColor: 'white',
          borderRadius: '16px',
          boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 9999,
          overflow: 'hidden',
          transition: 'all 0.3s ease',
        }}>
          {/* Header */}
          <div style={{
            backgroundColor: 'var(--brand, #20a490)',
            color: 'var(--brand-ink, #fff)',
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            flexShrink: 0,
          }}>
            <Bot size={20} />
            <span style={{ fontSize: '15px', fontWeight: 600, flex: 1 }}>
              Assistente Virtual
            </span>
            <button
              onClick={clearChat}
              title="Limpar conversa"
              style={{
                background: 'rgba(255,255,255,0.15)',
                border: 'none',
                color: 'var(--brand-ink, #fff)',
                cursor: 'pointer',
                borderRadius: '6px',
                padding: '4px 6px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Trash2 size={15} />
            </button>
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              title={isExpanded ? 'Minimizar' : 'Expandir'}
              style={{
                background: 'rgba(255,255,255,0.15)',
                border: 'none',
                color: 'var(--brand-ink, #fff)',
                cursor: 'pointer',
                borderRadius: '6px',
                padding: '4px 6px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {isExpanded ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
            </button>
          </div>

          {/* Messages */}
          <div style={{
            flex: 1,
            padding: '16px',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            backgroundColor: '#f8f9fa',
          }}>
            {messages.map((msg, idx) => (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                  flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
                }}
              >
                {/* Avatar */}
                <div style={{
                  width: '30px',
                  height: '30px',
                  borderRadius: '50%',
                  backgroundColor: msg.role === 'user' ? '#3498db' : 'var(--brand, #20a490)',
                  color: msg.role === 'user' ? '#fff' : 'var(--brand-ink, #fff)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  marginTop: '2px',
                }}>
                  {msg.role === 'user' ? <User size={14} /> : <Bot size={14} />}
                </div>

                {/* Bubble */}
                <div style={{
                  maxWidth: isExpanded ? '85%' : '78%',
                  padding: msg.role === 'user' ? '10px 14px' : '12px 16px',
                  borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                  backgroundColor: msg.role === 'user' ? 'var(--brand, #20a490)' : 'white',
                  color: msg.role === 'user' ? 'var(--brand-ink, #fff)' : '#333',
                  fontSize: '13.5px',
                  lineHeight: '1.6',
                  boxShadow: msg.role === 'user' ? 'none' : '0 1px 4px rgba(0,0,0,0.06)',
                  border: msg.role === 'user' ? 'none' : '1px solid #e8e8e8',
                }}>
                  {msg.role === 'user' ? (
                    <span>{msg.content}</span>
                  ) : (
                    <div className="chat-markdown">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {msg.content}
                      </ReactMarkdown>
                    </div>
                  )}
                  {msg.tool && (
                    <div style={{
                      marginTop: '8px',
                      fontSize: '11px',
                      color: '#999',
                      borderTop: '1px solid #eee',
                      paddingTop: '6px',
                    }}>
                      via {msg.tool}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {loading && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                <div style={{
                  width: '30px',
                  height: '30px',
                  borderRadius: '50%',
                  backgroundColor: 'var(--brand, #20a490)',
                  color: 'var(--brand-ink, #fff)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <Bot size={14} />
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 16px',
                  backgroundColor: 'white',
                  borderRadius: '16px 16px 16px 4px',
                  fontSize: '13px',
                  color: '#888',
                  border: '1px solid #e8e8e8',
                }}>
                  <Loader2 size={14} className="spin" />
                  <span>{loadingSteps[loadingStep]}</span>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div style={{
            padding: '12px 16px',
            backgroundColor: 'white',
            borderTop: '1px solid #eee',
            display: 'flex',
            gap: '8px',
            flexShrink: 0,
          }}>
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Digite sua pergunta..."
              disabled={loading}
              style={{
                flex: 1,
                padding: '10px 16px',
                borderRadius: '20px',
                border: '1px solid #ddd',
                fontSize: '13.5px',
                outline: 'none',
                backgroundColor: loading ? '#f5f5f5' : 'white',
                transition: 'border-color 0.2s',
              }}
              onFocus={(e) => e.target.style.borderColor = 'var(--brand, #20a490)'}
              onBlur={(e) => e.target.style.borderColor = '#ddd'}
            />
            <button
              onClick={sendMessage}
              disabled={loading || !input.trim()}
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                border: 'none',
                backgroundColor: 'var(--brand, #20a490)',
                color: 'var(--brand-ink, #fff)',
                cursor: loading || !input.trim() ? 'default' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: loading || !input.trim() ? 0.4 : 1,
                transition: 'opacity 0.2s',
              }}
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}

      {/* CSS para markdown e animacoes */}
      <style>{`
        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* Markdown dentro do chat */
        .chat-markdown p {
          margin: 0 0 8px 0;
        }
        .chat-markdown p:last-child {
          margin-bottom: 0;
        }
        .chat-markdown strong {
          font-weight: 600;
          color: #1a1a1a;
        }
        .chat-markdown ul, .chat-markdown ol {
          margin: 6px 0;
          padding-left: 20px;
        }
        .chat-markdown li {
          margin-bottom: 3px;
        }
        .chat-markdown h1, .chat-markdown h2, .chat-markdown h3,
        .chat-markdown h4, .chat-markdown h5, .chat-markdown h6 {
          margin: 10px 0 6px 0;
          font-weight: 600;
          color: #1a1a1a;
        }
        .chat-markdown h3 { font-size: 14px; }
        .chat-markdown h4 { font-size: 13px; }
        .chat-markdown blockquote {
          margin: 8px 0;
          padding: 6px 12px;
          border-left: 3px solid var(--brand, #20a490);
          background: #f0faf8;
          border-radius: 0 6px 6px 0;
          font-style: italic;
          color: #555;
        }
        .chat-markdown code {
          background: #f0f0f0;
          padding: 1px 5px;
          border-radius: 4px;
          font-size: 12px;
          font-family: 'Courier New', monospace;
        }
        .chat-markdown pre {
          background: #1e1e1e;
          color: #d4d4d4;
          padding: 10px;
          border-radius: 8px;
          overflow-x: auto;
          font-size: 12px;
          margin: 8px 0;
        }
        .chat-markdown pre code {
          background: none;
          padding: 0;
          color: inherit;
        }

        /* Tabelas */
        .chat-markdown table {
          width: 100%;
          border-collapse: collapse;
          margin: 10px 0;
          font-size: 12.5px;
          border-radius: 8px;
          overflow: hidden;
        }
        .chat-markdown thead {
          background: var(--brand, #20a490);
          color: var(--brand-ink, #fff);
        }
        .chat-markdown th {
          padding: 8px 10px;
          text-align: left;
          font-weight: 600;
          font-size: 12px;
          white-space: nowrap;
        }
        .chat-markdown td {
          padding: 7px 10px;
          border-bottom: 1px solid #eee;
          vertical-align: top;
        }
        .chat-markdown tbody tr:nth-child(even) {
          background: #f8f9fa;
        }
        .chat-markdown tbody tr:hover {
          background: #f0faf8;
        }

        /* Separadores */
        .chat-markdown hr {
          border: none;
          border-top: 1px solid #e0e0e0;
          margin: 10px 0;
        }

        /* Links */
        .chat-markdown a {
          color: var(--brand, #20a490);
          text-decoration: none;
        }
        .chat-markdown a:hover {
          text-decoration: underline;
        }
      `}</style>
    </>
  );
};

export default FloatingChat;
